import React, { useEffect, useState } from "react";
import { Dashboard as DashboardType, TeamDashboard } from "../objects/types";
import { getDashboard } from "../repository/api";

interface DashboardProps {
  year: number;
}

const Dashboard: React.FC<DashboardProps> = ({ year }) => {
  const [dashboard, setDashboard] = useState<DashboardType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getDashboard(year);
        setDashboard(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [year]);

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!dashboard || dashboard.teams.length === 0) {
    return (
      <div className="empty-state">
        No teams found. Add teams in the Admin panel.
      </div>
    );
  }

  const sortedTeams = [...dashboard.teams].sort((a, b) =>
    a.team.name.localeCompare(b.team.name)
  );

  return (
    <div>
      <h2>{year} Keeper Status</h2>
      <div className="team-grid">
        {sortedTeams.map((teamData) => (
          <TeamCard key={teamData.team.uuid} data={teamData} />
        ))}
      </div>
    </div>
  );
};

interface TeamCardProps {
  data: TeamDashboard;
}

const TeamCard: React.FC<TeamCardProps> = ({ data }) => {
  const { team, kp_balance, lifetime_keepers, one_year_keepers } = data;
  const totalKeepers = lifetime_keepers.length + one_year_keepers.length;

  return (
    <div className="team-card">
      <div className="team-name">{team.name}</div>
      <div>
        <span className={`kp-balance ${kp_balance < 0 ? "negative" : ""}`}>
          {kp_balance} KP remaining
        </span>
        <span style={{ marginLeft: "0.5rem", color: "var(--color-text-muted)" }}>
          ({totalKeepers} keepers)
        </span>
      </div>

      {lifetime_keepers.length > 0 && (
        <div className="keeper-section">
          <h4>Lifetime Keepers (3 KP each)</h4>
          <ul className="keeper-list">
            {lifetime_keepers.map((player) => (
              <li key={player.uuid} className="keeper-item lifetime">
                {player.name}
                <span className="tag">(L)</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {one_year_keepers.length > 0 && (
        <div className="keeper-section">
          <h4>One-Year Keepers (1 KP each)</h4>
          <ul className="keeper-list">
            {one_year_keepers.map((player) => (
              <li key={player.uuid} className="keeper-item">
                {player.name}
              </li>
            ))}
          </ul>
        </div>
      )}

      {totalKeepers === 0 && (
        <div className="empty-state" style={{ padding: "1rem 0" }}>
          No keepers yet
        </div>
      )}
    </div>
  );
};

export default Dashboard;
